import {
  createDefaultCropKeyframe,
  creatomateToNormalizedCrop,
} from '@src/editor/models/layout/CropTransformUtils';
import { LayoutSegment } from '@src/editor/models/layout/LayoutSegment';
import { LayoutTemplateRegistry } from '@src/editor/models/layout/LayoutTemplateRegistry';
import {
  buildBindingsForTemplate,
  findFallbackBinding,
} from '@src/editor/models/layout/templateUtils';
import { useEditor, useVideoCreatorStore } from '@src/stores-v2';

import { extractFitTypeFromTemplateId } from '../editor/models/layout/legacyLayoutMapping';
import { SceneService } from '../services/SceneService';
import { LogManager } from '../utility/Logger';

import type { Scene } from '@src/editor/models/layout/Scene';
import type {
  CropTransformKeyframe,
  LayoutTemplate,
} from '@src/editor/models/layout/types';

const logger = LogManager.getLogger('hook:useUpdateExistingScene');
const sceneService = new SceneService();

type UseUpdateExistingSceneReturn = {
  updateExistingScene: (
    scene: Scene,
    newTemplateId: string,
    elementSource?: Record<string, any>,
  ) => Promise<boolean>;
};

/**
 * Hook that provides a function to update an existing scene's template
 * with proper aspect-ratio-aware crop computation.
 */
export default function useUpdateExistingScene(): UseUpdateExistingSceneReturn {
  const videoCreator = useVideoCreatorStore();
  const editorStore = useEditor();

  /**
   * Computes normalized crop based on aspect ratio and fit type.
   * Uses legacy aspect-ratio logic to derive Creatomate-space rect.
   *
   * For non-landscape aspect ratios with 'cover' fit, adjusts the crop
   * to properly fill the frame. For all other cases, returns a default
   * full-frame crop.
   */
  async function computeNormalizedCropForFit(
    fitValue: string | null,
    elementSource?: Record<string, any>,
  ): Promise<CropTransformKeyframe['crop']> {
    const propertiesToUpdate: Record<string, string> = {
      fit: String(fitValue ?? ''),
    };

    if (!videoCreator.hasLandscapeAspectRatio()) {
      propertiesToUpdate.width = '100%';
      propertiesToUpdate.height = '100%';
      propertiesToUpdate.x = '50%';
      propertiesToUpdate.y = '50%';

      if (fitValue === 'cover' && elementSource) {
        await videoCreator.adjustModificationsToAspectRatio(
          propertiesToUpdate,
          {
            ...elementSource,
            fit: fitValue,
          },
          false,
        );
      }
    }

    const xPercent = parseFloat(propertiesToUpdate.x ?? '50');
    const yPercent = parseFloat(propertiesToUpdate.y ?? '50');
    const widthPercent = parseFloat(propertiesToUpdate.width ?? '100');
    const heightPercent = parseFloat(propertiesToUpdate.height ?? '100');

    return creatomateToNormalizedCrop(
      xPercent,
      yPercent,
      widthPercent,
      heightPercent,
    );
  }

  /**
   * Updates an existing scene with a new template, computing proper
   * aspect-ratio-aware crop for the bindings.
   *
   * @param scene - The scene to update
   * @param newTemplateId - The new template ID to apply
   * @param elementSource - Optional element source for cover crop adjustments
   * @returns Promise resolving to success boolean
   */
  async function updateExistingScene(
    scene: Scene,
    newTemplateId: string,
    elementSource?: Record<string, any>,
  ): Promise<boolean> {
    const newTemplate =
      LayoutTemplateRegistry.getInstance().getTemplate(newTemplateId);
    if (!newTemplate) {
      logger.warn(`Template ${newTemplateId} not found`);
      return false;
    }

    const normalizedCrop = await computeNormalizedCropForFit(
      extractFitTypeFromTemplateId(newTemplateId),
      elementSource ?? findElementSourceForScene(scene, newTemplate),
    );

    const layoutJson = scene.layout.toJSON();
    const bindings = buildBindingsForTemplate(
      newTemplate,
      layoutJson.bindings,
      editorStore.editor.getDefaultKaraokeConfig(),
      () => {
        return [
          createDefaultCropKeyframe({
            crop: normalizedCrop,
          }),
        ];
      },
    );

    if (!bindings) {
      throw new Error(
        'No suitable bindings could be built for the new template',
      );
    }

    const updatedLayout = new LayoutSegment({
      ...layoutJson,
      layoutTemplateId: newTemplateId,
      bindings,
    });

    const success = editorStore.editor.commandUpdateScene(scene.id, {
      layout: updatedLayout.toJSON(),
    });

    if (!success) {
      logger.warn(`Failed to update scene with template ${newTemplateId}`);
    }

    return success;
  }

  function findElementSourceForScene(
    scene: Scene,
    newTemplate: LayoutTemplate,
  ): Record<string, any> | undefined {
    // to find the trackId
    const fallbackBinding = findFallbackBinding(
      newTemplate,
      scene.layout.bindings,
    );
    if (!fallbackBinding) {
      logger.warn(
        `No suitable fallback binding found for template ${newTemplate.id} in scene ${scene.id}`,
      );
      return undefined;
    }

    const elements = sceneService.findElementsInScene(
      editorStore.editor.timeline,
      scene,
      fallbackBinding.trackId,
    );

    return elements?.[0]?.toJSON();
  }

  return {
    updateExistingScene,
  };
}
